import { z } from 'zod';

import {
    bytes32HexSchema,
    eoaAddressSchema,
    tradingSessionStatementSchema,
} from './schemas';

const credentialDescriptorSchema = z.object({
    id: z.string().min(1),
    type: z.literal('public-key'),
    transports: z.array(z.string()).optional(),
});

export const publicKeyCreationOptionsSchema = z.object({
    challenge: z.string().min(1),
    rp: z.object({ id: z.string().min(1), name: z.string().min(1) }),
    user: z.object({
        id: z.string().min(1),
        name: z.string().min(1),
        displayName: z.string(),
    }),
    pubKeyCredParams: z.array(
        z.object({ type: z.literal('public-key'), alg: z.literal(-7) }),
    ),
    timeout: z.number().positive().optional(),
    attestation: z.literal('none'),
    authenticatorSelection: z.object({
        residentKey: z.enum(['preferred', 'required']),
        userVerification: z.literal('required'),
        requireResidentKey: z.boolean().optional(),
    }),
    excludeCredentials: z.array(credentialDescriptorSchema).optional(),
});

export const passkeyRegistrationOptionsResponseSchema = z
    .object({
        challengeId: z.string().min(1),
        options: publicKeyCreationOptionsSchema,
    })
    .strict();

export const passkeyCredentialSchema = z
    .object({
        id: z.string().min(1),
        label: z.string().nullable(),
        publicKeyHash: bytes32HexSchema,
        status: z.enum(['pending', 'active', 'removed']),
        createdAt: z.string().datetime(),
        activatedAt: z.string().datetime().nullable(),
    })
    .strict();

export const passkeyListResponseSchema = z.array(passkeyCredentialSchema);

export const passkeyPossessionProofSchema = z
    .object({
        method: z.literal('passkey'),
        statement: tradingSessionStatementSchema,
        credentialId: z.string().min(1),
        authenticatorData: z.string().min(1),
        clientDataJson: z.string().min(1),
        signature: z.string().min(1),
    })
    .strict();

export const passkeyAssociationResponseSchema = z
    .object({
        signature: z.string().regex(/^0x[0-9a-fA-F]{130}$/u),
        nonce: z.string().regex(/^\d+$/u),
        issued: z.string().regex(/^\d+$/u),
        digest: bytes32HexSchema,
        signer: eoaAddressSchema,
        expiry: z.string().regex(/^\d+$/u),
        intent: z.enum(['create', 'link']),
        subject: eoaAddressSchema,
        accountHash: bytes32HexSchema,
    })
    .strict();
